import type { GameDefinition } from './types/game';
import { GameEngine } from './engine/GameEngine';
import type { StorageAdapter } from './engine/SaveManager';
import type { EngineEventListener } from './engine/events';
import { validateGame, formatIssues } from './schema/validate';
import { AdventureUI } from './render/AdventureUI';
import { el } from './render/dom';

/** Fuente del juego: objeto, URL a un JSON o función asíncrona. */
export type GameSource = GameDefinition | string | (() => Promise<GameDefinition>);

export interface MountOptions {
  /** Contenedor donde se monta el motor. */
  container: HTMLElement;
  /** Definición del juego, URL a un JSON o loader. */
  gameSource: GameSource;
  /** Identificador de instancia (permite varias instancias del mismo juego). */
  instanceId?: string;
  /** Prefijo para resolver las rutas de recursos del JSON. */
  assetBaseUrl?: string;
  /** Respeta la preferencia de movimiento reducido. */
  reducedMotion?: boolean;
  /** Reanudar una partida guardada en lugar de empezar una nueva. */
  resume?: boolean;
  /** Adaptador de almacenamiento (por defecto localStorage). */
  storage?: StorageAdapter;
  /** Callback opcional para eventos del motor. */
  onEvent?: EngineEventListener;
  /** Callback cuando el jugador pulsa «Salir» dentro del juego. */
  onExit?: () => void;
}

export interface AdventureEngineHandle {
  engine: GameEngine;
  /** Libera recursos: destruye Phaser, listeners y DOM creado. */
  destroy: () => void;
}

async function resolveSource(source: GameSource): Promise<GameDefinition> {
  if (typeof source === 'string') {
    const res = await fetch(source);
    if (!res.ok) throw new Error(`No se pudo cargar el juego desde ${source} (HTTP ${res.status}).`);
    return (await res.json()) as GameDefinition;
  }
  if (typeof source === 'function') {
    return await source();
  }
  return source;
}

/** Muestra un panel de error comprensible dentro del contenedor. */
function renderError(container: HTMLElement, title: string, detail: string): void {
  const box = el('div', { className: 'ae-root', attrs: { style: 'padding:24px;overflow:auto' } });
  box.append(
    el('div', { className: 'ae-modal' }, [
      el('h1', { textContent: title }),
      el('div', { className: 'ae-errors' }, [el('pre', { className: 'ae-error-detail', textContent: detail })])
    ])
  );
  container.append(box);
}

/**
 * Interfaz pequeña para montar adventurengine dentro de un contenedor.
 *
 * Valida el JSON, crea el motor y la interfaz, inicia o reanuda la partida y
 * devuelve un handle con `destroy()` para liberar recursos y listeners. No
 * depende de la estructura de ninguna página concreta.
 */
export async function mountAdventureEngine(opts: MountOptions): Promise<AdventureEngineHandle> {
  const { container } = opts;
  let def: GameDefinition;
  try {
    def = await resolveSource(opts.gameSource);
  } catch (err) {
    renderError(container, 'No se pudo cargar el juego', String((err as Error).message ?? err));
    return { engine: undefined as unknown as GameEngine, destroy: () => container.replaceChildren() };
  }

  const result = validateGame(def);
  if (!result.valid) {
    renderError(
      container,
      'El JSON del juego no es válido',
      formatIssues(result) || 'Error de validación desconocido.'
    );
    return { engine: undefined as unknown as GameEngine, destroy: () => container.replaceChildren() };
  }

  const engine = new GameEngine(def, {
    instanceId: opts.instanceId,
    storage: opts.storage,
    onEvent: opts.onEvent
  });

  const ui = new AdventureUI(container, {
    engine,
    assetBaseUrl: opts.assetBaseUrl ?? '',
    reducedMotion: opts.reducedMotion ?? false,
    onExit: opts.onExit
  });

  // Inicia o reanuda tras montar la UI para que escuche los eventos.
  let resumed = false;
  if (opts.resume && engine.hasSave()) {
    const load = engine.resumeGame();
    resumed = !!load.state && !load.reason;
  }
  if (!resumed) engine.newGame();

  return {
    engine,
    destroy: () => {
      ui.destroy();
      container.replaceChildren();
    }
  };
}
